import { Overlay, OverlayCtorArgs, ActionCloseType as ActionCloseTypeOverlay } from '@greenova/smartoverlay/dist/overlay';
import { envConfig } from "../../resources/env-config";
import { ActionHandler, IOpenedActionBase } from './ActionHandler';
import { IEngine } from '../IEngine';
import { ClientApi, FinalIssuedAction, IssuedOverlayAction, VisualConfig, DimensionsState, ActionType, ActionEventType, InnerAction, ActionCloseType, InnerActionType, OpenInnerAction, TriggerInnerAction, VerticalSnap, HorizontalSnap, Unit, RenderPlaceholderResult, IssuedHandover, ActionConfigType, ActionItemType, IssuedVoucherAction, PageElementDetection, TrackActionEventBase } from '../../api/client/clientApi';
import * as jschannel from "@greenova/jschannel";
import { getPureUrl } from '../../lib/helpers';
import { RequestType } from '../../SmartSignalsProfileDebugger/types/TrackingRequestResponseData';
import { ILogger } from '../../lib/logger';

interface IInnerAction {
    action: string;
}

interface IVeCommArgs {
    veCanOpen: boolean;
}

interface IOpenedOverlayAction extends IOpenedActionBase {
    overlay: OverlayEx;
    visualConf: VisualConfig;
}

// interface IInnerOpenAction {
//     //{ event: 'click', action: 'open', url: 'google.com', inNewWindow: true, closeAfter: true }
//     event: string;
//     action: string;
//     url: string;
//     target: string;
//     closeAfter: boolean;
// }

class OverlayEx extends Overlay {

    constructor(args: OverlayCtorArgs) {
        super(args);
    }

    public getChannel() {
        return this.chan;
    }
}

export class OverlayActionHandler extends ActionHandler {
    private static readonly HideGroupName: string = "__display-prevention";
    private openedActions: { [actionId: string]: IOpenedOverlayAction } = {};
    private hiddenGroups: (string | undefined)[] = [];
    private clientApi: ClientApi;
    private collectionActionGroups: any = {};

    private static readonly Target = {
        CURRENT: "current",
        NEW: "new",
        OVERLAY: "overlay"
    }

    private static readonly Action = {
        OPEN: "open"
    }


    constructor(engine: IEngine,
        private logger: ILogger) {
        super(engine);
        this.clientApi = new ClientApi(engine.engineUrl);
        if (engine.conf.cleanUpOnURLChange) {
            const that = this;
            if (window.history && !!window.history.pushState && !!window.history.replaceState) {
                let pushStateOrig = history.pushState;
                history.pushState = function (this: any) {
                    var res = pushStateOrig!.apply(this, <any>arguments);
                    try {
                        that.removeObsoleteActions();
                    }
                    catch (e) { }
                    return res;
                }

                let replaceStateOrig = history.replaceState;
                history.replaceState = function (this: any) {
                    var res = replaceStateOrig!.apply(this, <any>arguments);
                    try {
                        that.removeObsoleteActions();
                    }
                    catch (e) { }
                    return res;
                }
            }

            if (window.addEventListener)
                window.addEventListener("popstate", (ev) => this.removeObsoleteActions(), false);
            else if ((<any>window).attachEvent)
                (<any>window).attachEvent("onpopstate", () => this.removeObsoleteActions());
        }
    }

    public handleAction(issuedObject: SmartSignals.Models.Issued): void {
        let action = <FinalIssuedAction>issuedObject;
        let conf = <IssuedOverlayAction>action.conf;
        let visualConfig = <VisualConfig>conf.visualConfig;

        if (!Overlay.isBrowserSupported() ||
            action.actionId != undefined && this.openedActions[action.actionId] ||
            conf.collectionActionGroups && conf.collectionActionGroups.length && this.isCollectionActionGroupsActive(conf.collectionActionGroups)) {
            this.engine.unissuedAction(action);
            return;
        }

        this.displayOverlay(action, conf);
    }

    public showActions(hideGroup?: string): void {
        this.hiddenGroups = this.hiddenGroups.filter(x => x !== hideGroup);

        for (let actionId in this.openedActions) {
            this.openedActions[actionId].overlay.show(hideGroup);
        }
    }

    public hideActions(hideGroup?: string): void {
        if (this.hiddenGroups.indexOf(hideGroup) === -1)
            this.hiddenGroups.push(hideGroup);

        for (let actionId in this.openedActions) {
            this.openedActions[actionId].overlay.hide(hideGroup);
        }
    }

    private displayOverlay(action: FinalIssuedAction, conf: IssuedOverlayAction) {
        let visualConfig = <VisualConfig>conf.visualConfig;

        if (action.triggerUrl && getPureUrl(action.triggerUrl) !== getPureUrl(window.location.href)) {
            this.engine.unissuedAction(action);
            return;
        }
        //if (action.isInControlGroup) {
        //    this.onPanelOpened(action);
        //    return;
        //}

        let ctorArgs = <OverlayCtorArgs>{};

        ctorArgs.actionId = action.id;
        ctorArgs.cssClasses = ["SmartOverlay", "SmartOverlay_" + action.id];
        ctorArgs.TestMode = !action.isManual && action.isInControlGroup;

        ctorArgs.contentURL = conf.url;
        ctorArgs.closeButtonUrl = envConfig.overlayCloseButtonUrl;

        if (visualConfig.dimensions) {
            if (visualConfig.dimensions.width) {
                ctorArgs.width = visualConfig.dimensions.width.value;
                ctorArgs.widthUnit = this.getUnitString(visualConfig.dimensions.width.unit);
            }

            if (visualConfig.dimensions.height) {
                ctorArgs.height = visualConfig.dimensions.height.value;
                ctorArgs.heightUnit = this.getUnitString(visualConfig.dimensions.height.unit);
            }
        }

        ctorArgs.horizontalSnap = this.getHorizontalSnapString(visualConfig.horizontalSnap);
        ctorArgs.verticalSnap = this.getVerticalSnapString(visualConfig.verticalSnap);
        ctorArgs.dynamicDimensions = visualConfig.dimensionsState == DimensionsState.Dynamic;
        ctorArgs.modal = visualConfig.modal;
        ctorArgs.closeButtonVisible = visualConfig.closeButtonVisible;
        ctorArgs.closeOnOuterClick = visualConfig.closeOnOuterClick;
        ctorArgs.closeOnEscKey = true;

        ctorArgs.useMaxDynamicDimensions = visualConfig.maxDynamicDimensionsEnabled;
        ctorArgs.IOSFlexiblePositioning = visualConfig.flexiblePositionIOS;
        ctorArgs.transparent = visualConfig.transparent;

        if (visualConfig.slideIn) {
            ctorArgs.slideInEnabled = <boolean>visualConfig.slideIn.enabled;
            ctorArgs.slideInSide = String(visualConfig.slideIn.slideSide);
            ctorArgs.slideInTransition = <number>visualConfig.slideIn.transition;
        }

        if (visualConfig.slideOut) {
            ctorArgs.slideOutEnabled = <boolean>visualConfig.slideOut.enabled;
            ctorArgs.slideOutSide = String(visualConfig.slideOut.slideSide);
            ctorArgs.slideOutTransition = <number>visualConfig.slideOut.transition;
        }

        if (visualConfig.fadeIn) {
            ctorArgs.fadeInEnabled = <boolean>visualConfig.fadeIn.enabled;
            ctorArgs.fadeInTransition = <number>visualConfig.fadeIn.transition;
        }

        if (visualConfig.fadeOut) {
            ctorArgs.fadeOutEnabled = <boolean>visualConfig.fadeOut.enabled;
            ctorArgs.fadeOutTransition = <number>visualConfig.fadeOut.transition;
        }

        //ctorArgs.persistent = true;
        //ctorArgs.communicationRequired = action.communicationRequired;
        //ctorArgs.contentLoadTimeout = action.contentLoadTimeout; //TODO ?

        if (visualConfig.margin) {
            ctorArgs.marginTop = undefined;
            ctorArgs.marginBottom = undefined;
            ctorArgs.marginLeft = undefined;
            ctorArgs.marginRight = undefined;

            if (visualConfig.margin.top) {
                ctorArgs.marginTop = visualConfig.margin.top.value;
                ctorArgs.marginTopUnit = this.getUnitString(visualConfig.margin.top.unit);
            }
            if (visualConfig.margin.bottom) {
                ctorArgs.marginBottom = visualConfig.margin.bottom.value;
                ctorArgs.marginBottomUnit = this.getUnitString(visualConfig.margin.bottom.unit);
            }

            if (visualConfig.margin.left) {
                ctorArgs.marginLeft = visualConfig.margin.left.value;
                ctorArgs.marginLeftUnit = this.getUnitString(visualConfig.margin.left.unit);
            }
            if (visualConfig.margin.right) {
                ctorArgs.marginRight = visualConfig.margin.right.value;
                ctorArgs.marginRightUnit = this.getUnitString(visualConfig.margin.right.unit);
            }
        }

        if (visualConfig.boxShadow && visualConfig.boxShadow.enabled) {
            let bs = visualConfig.boxShadow;
            ctorArgs.boxShadow = {
                horizontalOffset: bs.horizontalOffset || 0,
                verticalOffset: bs.verticalOffset || 0,
                blur: bs.blur || 0,
                spread: bs.spread || 0,
                color: bs.color || "",
            };
        }

        ctorArgs.communicationRequired = action.type != ActionType.Voucher;

        let overlay: OverlayEx;
        if (action.type == ActionType.Voucher) {
            var veCanOpen = false;
            ctorArgs.channelSetup = (chan) => {
                chan.bind("veCanOpen", (trans: any, args: IVeCommArgs) => {
                    veCanOpen = args.veCanOpen;
                    if (veCanOpen) {
                        overlay.open();
                        this.trySetVariable(<IssuedVoucherAction>action.conf, chan);
                    }
                    else {
                        this.onPanelDiscarded(action);
                        overlay.destroy();
                    }
                });
            };

            overlay = new OverlayEx(ctorArgs);
            overlay.onOpening = () => {
                return veCanOpen;
            };

        }
        else {
            overlay = new OverlayEx(ctorArgs);
        }

        overlay.onOpen = () => {
            let eventData: IEventData = { actionId: action.id };
            this.engine.trigger("action:overlay:displayed", eventData);

            this.onPanelOpened(action);

            if (action.issuedFollowUpActions) {
                for (let issuedFollowUpAction of action.issuedFollowUpActions) {

                    let handler = this.engine.getActionHandler((<any>issuedFollowUpAction).type);
                    if (handler)
                        handler.handle(issuedFollowUpAction);
                }
            }
            if (!action.isManual && action.isInControlGroup)
                overlay!.close();
        };
        overlay.onClose = (actionCloseType?: ActionCloseTypeOverlay) => {
            let eventData: IEventData = { actionId: action.id };
            this.engine.trigger("action:overlay:closed", eventData);

            this.onPanelClosed(action, actionCloseType);
        };

        overlay.onCloseDone = () => {
            if (action.actionId != undefined) {
                if (this.openedActions[action.actionId]) {
                    this.openedActions[action.actionId].stopElementDetection?.();
                    delete this.openedActions[action.actionId];
                }
            }
            this.clearCollectionActionGroups(conf.collectionActionGroups);
            if (overlay)
                overlay.destroy();
        };

        overlay.onPanelAction = (innerAction: any, trans: any) => {
            if (!innerAction || innerAction.method == "clicked")
                return;

            if (innerAction.method == "userProfile") {
                if (innerAction.placeholder && action.organizationId && action.collectionId && action.userId) {
                    trans.delayReturn(true);
                    this.getDynamicContent(action.organizationId, action.collectionId, action.userId, innerAction.placeholder).then(result => {
                        if (trans)
                            trans.complete(result);
                    });
                }
                return;
            }

            this.onPanelAction(action, innerAction, overlay);
        }
        overlay.onContentLoaded = () => {
            this.onPanelContentLoaded(action);
        };

        if (action.actionId != undefined) {
            const openedAction: IOpenedOverlayAction = {
                overlay: overlay,
                action: action,
                visualConf: visualConfig,
                timeoutId: 0,
                actionOpenedOnOrigin: location.origin,
                actionOpenedOnPath: location.pathname
            };

            this.openedActions[action.actionId] = openedAction;

            overlay.onReadyToShow = () => {
                openedAction.stopElementDetection = this.showWhenAllowed(openedAction);
            };
        }

        this.activateCollectionActionGroups(conf.collectionActionGroups);

        overlay.hide(OverlayActionHandler.HideGroupName);
        overlay.open();
    }

    private prepareActionEventData(action: FinalIssuedAction, eventType: ActionEventType): TrackActionEventBase {
        const conf = <IssuedOverlayAction>action.conf;
        return {
            actionEventType: eventType,
            itemType: conf.itemType,
            itemId: conf.itemId,
            languageId: conf.languageId,
            geoId: conf.geoId,
            templateTypeId: conf.templateTypeId,
            templateVersion: conf.templateVersion
        };
    }

    private onPanelDiscarded(action: FinalIssuedAction) {
        const data = this.prepareActionEventData(action, ActionEventType.OverlayDiscarded);
        this.sendActionEvent(data, action);
    }

    private onPanelOpened(action: FinalIssuedAction) {
        const data = this.prepareActionEventData(action, ActionEventType.OverlayOpened);
        this.sendActionEvent(data, action).then(response => {
            if (response) {
                this.handleHandovers(response.handovers);
            }
        });
    }

    private onPanelClosed(action: FinalIssuedAction, actionCloseType?: ActionCloseTypeOverlay) {
        const data = this.prepareActionEventData(action, ActionEventType.OverlayClosed);
        data.actionCloseType = actionCloseType ? <ActionCloseType>actionCloseType.valueOf() : undefined;

        this.sendActionEvent(data, action).then(innerActionData => {
            if (!innerActionData)
                return;

            if (innerActionData.innerActionType == InnerActionType.CloseButton) {
                var triggerInnerAction = <TriggerInnerAction>innerActionData;
                if (triggerInnerAction.issuedAction) {
                    let handler = this.engine.getActionHandler(triggerInnerAction.issuedAction.type);
                    if (handler)
                        handler.handle(triggerInnerAction.issuedAction);
                }
            }
        });
    }

    private onPanelAction(action: FinalIssuedAction, innerAction: IInnerAction, overlay: Overlay) {
        const data = this.prepareActionEventData(action, ActionEventType.Trigger);
        data.innerActionName = innerAction.action;

        this.sendActionEvent(data, action).then(innerActionData => {
            if (!innerActionData)
                return;
            if (innerActionData.innerActionType == InnerActionType.Open) {
                var openInnerAction = <OpenInnerAction>innerActionData
                if (openInnerAction.url) {
                    if (openInnerAction.newWindow)
                        window.open(openInnerAction.url);
                    else
                        location.href = openInnerAction.url;
                }
            }
            if (innerActionData.innerActionType == InnerActionType.Trigger) {
                var triggerInnerAction = <TriggerInnerAction>innerActionData;
                if (triggerInnerAction.issuedAction) {
                    let handler = this.engine.getActionHandler(triggerInnerAction.issuedAction.type);
                    if (handler)
                        handler.handle(triggerInnerAction.issuedAction);
                }
            }
            if (innerActionData.closeOverlay && overlay)
                overlay.close(ActionCloseTypeOverlay.innerActionClose);

            if (innerActionData) {
                this.handleHandovers(innerActionData.handovers);
            }
        });
    }

    private sendActionEvent(data: TrackActionEventBase, action: FinalIssuedAction): Promise<InnerAction | undefined> {
        if (!action.userId || !action.organizationId || !action.collectionId || !action.campaignId || !action.actionId || !action.id)
            return Promise.resolve(undefined);

        const promise = this.clientApi.actionEvent_Post_3(
            action.userId,
            action.organizationId,
            action.collectionId,
            action.campaignId,
            action.actionId,
            action.id,
            data);

        return this.engine.trackApiCall(RequestType.OverlayEvent, data, promise);
    }

    private onPanelContentLoaded(action: FinalIssuedAction) {
    }

    // private isOpenAction(innerAction: IInnerAction): innerAction is IInnerOpenAction {
    //     return (<IInnerOpenAction>innerAction).url !== undefined;
    // }

    private getVerticalSnapString(enumValue?: VerticalSnap): string | undefined {
        switch (enumValue) {
            case VerticalSnap.Top:
                return Overlay.verticalSnap.TOP;
            case VerticalSnap.Bottom:
                return Overlay.verticalSnap.BOTTOM;
            case VerticalSnap.Center:
                return Overlay.verticalSnap.CENTER;
            default:
                return undefined;
        }
    }

    private getHorizontalSnapString(enumValue?: HorizontalSnap): string | undefined {
        switch (enumValue) {
            case HorizontalSnap.Left:
                return Overlay.horizontalSnap.LEFT;
            case HorizontalSnap.Right:
                return Overlay.horizontalSnap.RIGHT;
            case HorizontalSnap.Center:
                return Overlay.horizontalSnap.CENTER;
            default:
                return undefined;
        }
    }

    private getUnitString(enumValue?: Unit): string | undefined {
        switch (enumValue) {
            case Unit.Percent:
                return Overlay.unit.PERCENTAGE;
            case Unit.Pixels:
                return Overlay.unit.PIXEL;
            default:
                return undefined;
        }
    }

    private getDynamicContent(organizationId: string, collectionId: string, userId: string, placeholder: string): PromiseLike<RenderPlaceholderResult | null> {
        return this.clientApi.resolvePlaceholder_Post_3(userId, organizationId, collectionId, { placeholder: placeholder });
    }

    public isCollectionActionGroupsActive(groups?: string[]): boolean {
        if (groups) {
            for (let group of groups) {
                if (this.collectionActionGroups && this.collectionActionGroups[group])
                    return true;
            }
        }
        return false;
    }

    public activateCollectionActionGroups(groups?: string[]): boolean {
        if (groups) {
            if (this.isCollectionActionGroupsActive(groups)) {
                return false;
            }

            for (let group of groups) {
                this.collectionActionGroups[group] = true;
            }
        }
        return true;
    }

    public clearCollectionActionGroups(groups?: string[]) {
        if (groups) {
            for (let group of groups) {
                this.collectionActionGroups[group] = false;
            }
        }
    }

    private removeObsoleteActions() {
        for (let key in this.openedActions) {
            var openedAction = this.openedActions[key];

            if (openedAction.actionOpenedOnOrigin != location.origin || openedAction.actionOpenedOnPath != location.pathname) {
                openedAction.overlay.close(ActionCloseTypeOverlay.systemClose);
            }
        }
    }

    private handleHandovers(handovers: IssuedHandover[] | undefined) {
        if (!handovers)
            return;

        for (var handover of handovers) {
            if (!handover.conf || !handover.conf.dataExchangeType)
                continue;

            let handler = this.engine.getHandoverHandler(handover.conf.dataExchangeType);
            if (handler)
                handler.handle(handover);
        }
    }

    private trySetVariable(conf: IssuedVoucherAction, channel: jschannel.Channel) {
        if (conf.extractClientSideValue) {
            try {
                var variableValue = eval(conf.clientSideVariable!);

                if (variableValue === undefined || variableValue === null)
                    return;

                channel.call({
                    method: "setVariable",
                    params: {
                        variableName: conf.voucherVariableName,
                        variableValue: eval(conf.clientSideVariable!)
                    },
                    success: () => { },
                    error: () => { }
                });
            }
            catch {
            }
        }
    }

    private showWhenAllowed(openedAction: IOpenedOverlayAction): (() => void) | undefined {
        if (this.hiddenGroups.length) {
            this.hiddenGroups.forEach(x => openedAction.overlay.hide(x));
        }

        if (openedAction.visualConf.displayPreventionDisabled || !openedAction.action.pageElementDetection?.querySelectors?.length) {
            openedAction.overlay.show(OverlayActionHandler.HideGroupName);
            return undefined
        }

        const checkVisibility = () => this.startThrottling(
            openedAction,
            openedAction.overlay.isModal(),
            openedAction.overlay.getRootRect(),
            () => openedAction.overlay.show(OverlayActionHandler.HideGroupName),
            () => openedAction.overlay.hide(OverlayActionHandler.HideGroupName)
        );

        window.addEventListener('load', checkVisibility, false);
        window.addEventListener('scroll', checkVisibility, false);
        window.addEventListener('resize', checkVisibility, false);
        openedAction.overlay.onShowTrigger = checkVisibility;
        checkVisibility();

        let disposed = false;

        return () => {
            if (disposed)
                return;

            clearTimeout(openedAction.timeoutId);
            window.removeEventListener('load', checkVisibility, false);
            window.removeEventListener('scroll', checkVisibility, false);
            window.removeEventListener('resize', checkVisibility, false);
            openedAction.overlay.onShowTrigger = undefined;
            disposed = true;
        };
    }
}
